/**
 * Password Overlay Component
 *
 * A custom web component that creates a password protection overlay for pages.
 * When initialized, it hides all other page content and stores it in memory.
 * Upon correct password entry, it reveals the hidden content.
 */

/* global HTMLElement, sessionStorage */

import { createDebugger } from '../utils/debug.js';

const debug = createDebugger('password-overlay', true);

class PasswordOverlay extends HTMLElement {
    constructor() {
        super();
        this.hiddenContent = [];
        debug.log('Component constructed');
    }

    connectedCallback() {
        debug.log('Component connected to DOM');

        // Prevent duplicate initialization
        if (this.hasAttribute('data-initialized')) {
            debug.log('Already initialized, skipping');
            return;
        }
        this.setAttribute('data-initialized', 'true');

        this.correctPassword = this.dataset.password;
        this.sectionId = this.dataset.sectionId;
        this.ignoreCase = this.dataset.ignoreCase === 'true';

        debug.log('Section ID:', this.sectionId);
        debug.log('Password configured:', !!this.correctPassword);
        debug.log('Ignore case:', this.ignoreCase);

        // Get DOM elements
        this.passwordInput = this.querySelector('input[name="page-password"]');
        this.submitBtn = this.querySelector('#password-submit-btn');
        this.errorDiv = this.querySelector('#password-error');

        debug.log('DOM elements found:', {
            input: !!this.passwordInput,
            button: !!this.submitBtn,
            errorDiv: !!this.errorDiv
        });

        // Check if already unlocked this session
        const isUnlocked = sessionStorage.getItem(`password-overlay-unlocked-${this.sectionId}`);

        if (isUnlocked === 'true') {
            debug.log('Already unlocked in this session, removing overlay');
            // Already unlocked, just remove overlay
            this.remove();
        } else {
            debug.log('Not unlocked, hiding page content');
            // Hide all other content
            this.hidePageContent();

            // Setup event listeners
            this.setupEventListeners();

            // Focus on password input
            setTimeout(() => {
                this.passwordInput?.focus();
                debug.log('Password input focused');
            }, 100);
        }
    }

    setupEventListeners() {
        debug.log('Setting up event listeners');

        // Submit button click
        this.submitBtn?.addEventListener('click', () => {
            debug.log('Submit button clicked');
            this.verifyPassword();
        });

        // Enter key press
        this.passwordInput?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                debug.log('Enter key pressed');
                e.preventDefault();
                this.verifyPassword();
            }
        });
    }

    hidePageContent() {
        debug.log('Hiding page content');
        const main = document.querySelector('main');

        if (!main) {
            debug.error('Main element not found, cannot hide content');
            return;
        }

        debug.log('Found main element:', main.id || main.className);
        const children = Array.from(main.children);
        let hiddenCount = 0;

        // Find which section contains this password overlay
        const overlaySection = this.closest('section');
        debug.log('Password overlay is inside section:', overlaySection?.className);

        children.forEach((child) => {
            // Skip the section that contains the password overlay
            if (child === overlaySection) {
                debug.log('Skipping section containing password overlay');
                return;
            }

            // Store the element and its original display value
            const originalDisplay = child.style.display;
            this.hiddenContent.push({
                element: child,
                originalDisplay: originalDisplay
            });

            debug.log('Hiding element:', child.tagName, child.id || child.className);
            // Hide with CSS instead of removing from DOM
            child.style.display = 'none';
            hiddenCount++;
        });

        debug.log(`Hidden ${hiddenCount} elements from main (CSS display:none)`);
    }

    restorePageContent() {
        debug.log(`Restoring ${this.hiddenContent.length} hidden elements`);

        this.hiddenContent.forEach((item, index) => {
            // Restore original display value
            if (item.originalDisplay) {
                item.element.style.display = item.originalDisplay;
            } else {
                item.element.style.display = '';
            }
            debug.log(`Restored element ${index + 1} display`);
        });

        debug.log('All content restored, removing overlay');
        // Remove the overlay
        this.remove();

        // Clear the stored content
        this.hiddenContent = [];
    }

    verifyPassword() {
        const enteredPassword = this.passwordInput?.value.trim();
        debug.log('Verifying password');

        // Compare passwords based on case sensitivity setting
        const passwordsMatch = this.ignoreCase
            ? enteredPassword.toLowerCase() === this.correctPassword.toLowerCase()
            : enteredPassword === this.correctPassword;

        if (passwordsMatch) {
            debug.log('Password correct! Unlocking content');

            // Hide error if shown
            this.errorDiv?.classList.add('hidden');

            // Restore content
            this.restorePageContent();

            // Store success in sessionStorage
            sessionStorage.setItem(`password-overlay-unlocked-${this.sectionId}`, 'true');
            debug.log('Unlock state saved to sessionStorage');
        } else {
            debug.warn('Incorrect password entered');

            // Show error
            this.errorDiv?.classList.remove('hidden');

            // Shake animation using Tailwind
            this.classList.add('animate-shake');
            setTimeout(() => {
                this.classList.remove('animate-shake');
            }, 500);

            // Clear input
            if (this.passwordInput) {
                this.passwordInput.value = '';
                this.passwordInput.focus();
            }
        }
    }
}

// Register the custom element
if (!window.customElements.get('password-overlay')) {
    window.customElements.define('password-overlay', PasswordOverlay);
    debug.log('Custom element registered');
}
